import React, { useState, useEffect } from 'react';
import { charactersAPI } from '../services/api';
import { Character } from '../types';
import CharacterCard from '../components/CharacterCard';
import CreateCharacterModal from '../components/CreateCharacterModal' ;
import CharacterSidePanel from '../components/CharacterSidePanel';
import ElementPreviewPanel from '../components/ElementPreviewPanel';
import { useDataCache } from '../hooks/useDataCache';
import { usePageLoading } from '../hooks/usePageLoading';
import LoadingOverlay from '../components/LoadingOverlay';

import '../styles/Characters.css';

const Characters: React.FC = () => {
  
  // Use caching hook for characters data
  const {
    data: characters = [],
    isLoading: charactersLoading,
    error: charactersError,
    fetchData: fetchCharacters,
    preloadData: preloadCharacters
  } = useDataCache<Character[]>({
    cacheKey: 'characters',
    fetchFunction: charactersAPI.getAll,
    cacheDuration: 5 * 60 * 1000, // 5 minutes
    autoRefresh: true,
    preloadCondition: () => true // Always preload characters
  });

  const [filteredCharacters, setFilteredCharacters] = useState<Character[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest'>('date-created');
  
  // Global tab selection state
  const [globalTabSelection, setGlobalTabSelection] = useState<string>('summary');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showElementPanel, setShowElementPanel] = useState(false);
  const [showElementPreview, setShowElementPreview] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [updatedCharacterIds, setUpdatedCharacterIds] = useState<Set<string>>(new Set());

  // Use page loading hook - wait for characters data to load
  const { isLoading: pageLoading } = usePageLoading({
    dependencies: [
      characters !== undefined, // Characters data has been fetched
      !charactersLoading // Characters are no longer loading
    ],
    onLoadComplete: () => {
      console.log('✅ Characters page loaded successfully');
    }
  });



  // Monitor characters array changes for debugging (only log significant changes)
  useEffect(() => {
    if ((characters || []).length > 0) {
      console.log('🔄 Characters: Characters loaded:', {
        total: (characters || []).length,
        withImageUrl: (characters || []).filter(c => c.imageUrl).length
      });
    }
  }, [(characters || []).length, characters]);



  // Filter and sort characters whenever characters, searchTerm, or sortBy changes
  useEffect(() => {
    let filtered = [...(characters || [])];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(character =>
        character.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'date-created':
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'date-created-oldest':
        filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
    }

    setFilteredCharacters(filtered);
  }, [characters, searchTerm, sortBy]);

  const handleUpdateCharacter = (updatedCharacter: Character) => {
    console.log('🔄 Characters: Updating character:', updatedCharacter._id);
    
    // Refresh all characters to ensure linked arrays are current
    const refreshAllCharacters = async () => {
      try {
        console.log('🔄 Characters: Refreshing all characters after update...');
        await fetchCharacters(true); // Force refresh
        console.log('🔄 Characters: All characters refreshed successfully');
      } catch (error) {
        console.error('🔄 Characters: Failed to refresh all characters:', error);
      }
    };
    
    refreshAllCharacters();
    
    setUpdatedCharacterIds(prev => new Set([...Array.from(prev), updatedCharacter._id]));
    
    // Clear the updated flag after a delay
    setTimeout(() => {
      setUpdatedCharacterIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(updatedCharacter._id);
        return newSet;
      });
    }, 2000);
  };

  const handleDeleteCharacter = async (characterId: string) => {
    try {
      console.log('🔄 Characters: Deleting character:', characterId);
      await charactersAPI.delete(characterId);
      await fetchCharacters(true); // Force refresh
      console.log('✅ Characters: Character deleted successfully');
      // Show success toast only after successful delete
      if (typeof window.showToast === 'function') {
        window.showToast('Character deleted successfully!', 'success');
      }
    } catch (error) {
      console.error('❌ Characters: Failed to delete character:', error);
      // Show error toast only on failure
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to delete character. Please try again.', 'error');
      }
    }
  };



  const handleOpenPreviewPanel = (elementIds: string[]) => {
    // console.log('🔄 Characters: Opening preview panel for elements:', elementIds);
    setShowElementPreview(true);
  };

  const handleCloseSidePanel = () => {
    setShowElementPanel(false);
    setSelectedCharacter(null);
  };

  const handleClosePreviewPanel = () => {
    setShowElementPreview(false);
    setSelectedCharacter(null);
  };

  return (
    <>
      {/* Loading Overlay */}
      <LoadingOverlay isLoading={pageLoading} message="Loading characters..." />
      
      <div className="overview">
        {/* Header - Similar to Overview page */}
        <div className="overview-header">
          <h1 className="overview-title">Characters</h1>
          <p className="overview-subtitle">Manage your world's characters</p>
        </div>

        {/* Header with actions - similar to old overview */}
        <div className="overview-actions">
          <button 
            className="btn btn--green" 
            onClick={() => setShowCreateModal(true)}
          >
            Create New Character
          </button>
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="live-search"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest')}
            className="sort-select"
          >
            <option value="date-created">Newest First</option>
            <option value="date-created-oldest">Oldest First</option>
            <option value="name-asc">A to Z</option>
            <option value="name-desc">Z to A</option>
          </select>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          {/* Left side - Filter dropdowns */}
          {/* <div className="filter-left">
            <button
              className="btn btn--clear-filters"
              onClick={() => {
                setSearchTerm('');
              }}
            >
              Clear Filters
            </button>
          </div> */}

          {/* Right side - Tab selection */}
          <div className="filter-right">
            <div className="filter-group">
              <label htmlFor="tab-filter" className="filter-label">Select Default Tab:</label>
              <select
                id="tab-filter"
                value={globalTabSelection}
                onChange={(e) => setGlobalTabSelection(e.target.value)}
                className="filter-select tab-select"
              >
                <option value="summary">Summary</option>
                <option value="features">Features</option>
                <option value="attributes">Attributes</option>
                <option value="equipment">Equipment</option>
                <option value="threads">Threads</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Card Container - Outside overview div */}
      <div className="card-container" id="results-container">
        {Array.isArray(filteredCharacters) && filteredCharacters.length > 0 ? (
          filteredCharacters.map(character => (
            <CharacterCard
              key={`${character._id}-${character.imageUrl || character.image || 'default'}-${updatedCharacterIds.has(character._id) ? 'updated' : 'stable'}`}
              character={character}
              onUpdate={handleUpdateCharacter}
              onDelete={handleDeleteCharacter}

              onOpenPreviewPanel={handleOpenPreviewPanel}
              defaultTab={globalTabSelection}
            />
          ))
        ) : (
          <div className="no-results">
            <p>No characters found.</p>
          </div>
        )}
      </div>

      {/* Create Character Modal */}
      {showCreateModal && (
        <CreateCharacterModal
          onClose={() => setShowCreateModal(false)}
          onCharacterCreated={async (newCharacter) => {
            await fetchCharacters(true); // Force refresh
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Side Panel */}
      {showElementPanel && selectedCharacter && (
        <CharacterSidePanel
          isOpen={showElementPanel}
          onClose={handleCloseSidePanel}
          character={selectedCharacter}
          field=""
          allElements={[]}
          onUpdate={handleUpdateCharacter}
        />
      )}

      {/* Element Preview Panel */}
      {showElementPreview && (
        <ElementPreviewPanel
          isOpen={showElementPreview}
          onClose={handleClosePreviewPanel}
        />
      )}
    </>
  );
};

export default Characters; 